<template>
  <RevCard class="relative p-24">
    <div
      v-if="tagVariant"
      class="bg-surface-brand-hi text-static-brand-mid rounded-lg body-2-bold absolute -top-24 right-24 px-16 py-8"
    >
      {{ tagVariant }}
    </div>
    <div class="flex flex-col justify-between">
      <div class="mb-24 flex items-center gap-16">
        <div class="border-static-default-low rounded-md shrink-0 border p-8">
          <IconSwap size="24" />
        </div>
        <div class="w-full">
          <div class="body-1-bold flex items-center gap-8">
            {{
              i18n(translations.buybackSwapSummaryTradeInValue, {
                price: i18n.price(price),
              })
            }}
            <RevButtonBase class="shrink-0 md:hidden" @click="openSummaryModal">
              <IconInfo size="16" />
            </RevButtonBase>
            <RevButton
              class="ml-auto hidden md:block"
              data-qa="swap-summary-delete"
              data-test="swap-summary-delete"
              size="small"
              variant="secondaryDestructive"
              @click="handleDelete"
            >
              {{ i18n(translations.buybackSwapSummaryTradeInRemove) }}
            </RevButton>
          </div>
          <div class="body-2">{{ title }}</div>
        </div>
      </div>
      <RevDivider class="mb-24" />
      <div
        class="mb-24 grid grid-cols-1 gap-8 md:mb-0 md:grid-cols-2 md:gap-12"
      >
        <div
          v-for="{ icon, label } in tradeInDetails"
          :key="label"
          class="flex items-center gap-12"
        >
          <component :is="icon" class="shrink-0" size="24" />
          <div class="text-static-default-low body-2">{{ label }}</div>
        </div>
      </div>
      <RevButton
        class="ml-auto md:hidden"
        size="small"
        variant="secondaryDestructive"
        @click="handleDelete"
      >
        {{ i18n(translations.buybackSwapSummaryTradeInRemove) }}
      </RevButton>
      <RevButtonBase
        class="body-2-link ml-auto hidden md:block"
        @click="openSummaryModal"
      >
        {{ i18n(translations.buybackSwapSummaryTradeInLearnMore) }}
      </RevButtonBase>
      <SummaryCartModal :price :shipping-mode :total-price-after-buyback />
    </div>
  </RevCard>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import { BUYBACK_SHIPPING_MODE } from '@backmarket/http-api/src/api-specs-buyback/constants'
import type { GetOfferResponseLegacy } from '@backmarket/http-api/src/api-specs-buyback/customer/getOfferLegacy'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { openModal } from '@ds/components/ModalBase'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconMoney } from '@ds/icons/IconMoney'
import { IconPackage } from '@ds/icons/IconPackage'
import { IconSwap } from '@ds/icons/IconSwap'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'

import { MODAL_NAMES } from '~/scopes/buyback/constants'

import SummaryCartModal from './SummaryCartModal.vue'
import translations from './SwapSummaryCart.translations'

const i18n = useI18n()
const { trackClick } = useTracking()
const experiments = useExperiments()

export interface SwapSummaryProps {
  title: string
  price: Price
  shippingMode: GetOfferResponseLegacy['defaultShippingModeId'] | undefined
  totalPriceAfterBuyback?: Price
}

const { shippingMode = undefined } = defineProps<SwapSummaryProps>()

const emit = defineEmits<{
  (event: 'delete-swap'): void
}>()

function handleDelete() {
  emit('delete-swap')
}

const tradeInDetailsShipping = computed(() => {
  switch (shippingMode) {
    case BUYBACK_SHIPPING_MODE.NEW_KIT:
      return {
        icon: IconPackage,
        label: i18n(translations.buybackSwapSummaryTradeMailing),
      }
    case BUYBACK_SHIPPING_MODE.QR_CODE:
      return {
        icon: IconPackage,
        label: i18n(translations.buybackSwapSummaryTradeMailingQrCode),
      }
    default:
      return {
        icon: IconDownload,
        label: i18n(translations.buybackSwapSummaryTradeNoMailing),
      }
  }
})

const tradeInDetails = computed(() => {
  return [
    tradeInDetailsShipping.value,
    {
      icon: IconTruckExpress,
      label: i18n(translations.buybackSwapSummaryTradeInShipping),
    },
    {
      icon: IconMoney,
      label: i18n(translations.buybackSwapSummaryTradeInMoney),
    },
  ]
})

const tagVariant = computed(() => {
  switch (experiments['experiment.swapCartPersuasionTactics']) {
    case 'price_tag_no_modal':
    case 'price_tag_price_modal':
    case 'price_tag_commital_modal':
      return i18n(translations.buybackSwapSummaryTradeInBestOffer)
    case 'social_tag_no_modal':
    case 'social_tag_price_modal':
    case 'social_tag_commital_modal':
      return i18n(translations.buybackSwapSummaryTradeInTrades)
    default:
      return undefined
  }
})

const openSummaryModal = () => {
  trackClick({
    zone: 'cart_swapBlock',
    name: 'swap_cart_modal',
  })
  openModal(MODAL_NAMES.SWAP_SUMMARY)
}
</script>
